import { isEmpty } from "lodash";
import countryList from "../components/data/CountryList";
import CountryType from "../types/CountryType";
import { commaAndFormatStringList } from "./textUtils";
import { typedObjectEntries, typedObjectKeys } from "./typeCheckUtils";

//Aggregate proxy count in country type object.
export function getAggregateProxyCount(
  countries: CountryType | null | undefined
): number {
  if (!countries || isEmpty(countries)) {
    return 0;
  }
  return Object.values(countries).reduce((a, b) => a + b);
}

function convertUnallocatedCountryNames(
  unallocatedCountries: CountryType | null | undefined
) {
  if (isEmpty(unallocatedCountries)) {
    return [];
  }
  return typedObjectKeys(unallocatedCountries).map((k) => countryList[k]);
}

export function getUnallocatedProxyText(
  unallocatedCountries: CountryType | null | undefined
): string {
  const unallocatedCountryNames =
    convertUnallocatedCountryNames(unallocatedCountries);
  if (unallocatedCountryNames.length == 0) {
    return "All proxies are allocated.";
  }

  const cumUnallocatedCount = getAggregateProxyCount(unallocatedCountries);

  return `${cumUnallocatedCount.toLocaleString()} proxies in your list are no longer available.`;
}

export function purifyCountryList(countries: CountryType | undefined) {
  if (!countries) return;

  return typedObjectEntries(countries).reduce<CountryType>((acc, entry) => {
    const [key, value] = entry || [];
    if (value && value > 0 && key) {
      acc = { ...acc, [key]: value };
    }
    return acc;
  }, {});
}

export function removeWorldCountries(countries: CountryType) {
  const editedCountries = { ...countries };
  delete editedCountries["ZZ"];
  return editedCountries;
}

//Adds unallocated proxies to ZZ key.
export function backendFormatCountryList(
  selectedCountries: CountryType | undefined,
  unallocatedProxiesCount: number
) {
  const purifiedCountries = purifyCountryList(selectedCountries);
  let newCountries: CountryType = { ...purifiedCountries, ZZ: 0 };

  const unassignedAllocatedProxies =
    unallocatedProxiesCount - getAggregateProxyCount(newCountries);
  // Add unassigned with ZZ key.
  if (unassignedAllocatedProxies > 0) {
    newCountries.ZZ = unassignedAllocatedProxies;
  } else {
    //To prevent error on backend.
    //"message":"Proxy count for ZZ must be greater or equal to 1.
    delete newCountries.ZZ;
  }
  return newCountries;
}

export function getUnallocatedReplaceText(
  countries: CountryType,
  unallocatedProxiesCount: number
) {
  const selectedCountries = purifyCountryList(countries);
  const selectedCoutriesCount = isEmpty(selectedCountries)
    ? 0
    : Object.values(selectedCountries).reduce((a, b) => a + b);

  if (selectedCoutriesCount === 0) {
    return `${unallocatedProxiesCount.toLocaleString()} proxies will be assigned from all around the world.`;
  }

  const unAllocatedCountryNames =
    selectedCountries &&
    typedObjectKeys(selectedCountries).map((key) => {
      return countryList[key];
    });

  if (
    unAllocatedCountryNames &&
    unallocatedProxiesCount - selectedCoutriesCount === 0
  ) {
    return `${selectedCoutriesCount.toLocaleString()} proxies will be assigned from ${commaAndFormatStringList(
      unAllocatedCountryNames
    )}.`;
  }

  if (!unAllocatedCountryNames) return "";

  return `${selectedCoutriesCount.toLocaleString()} proxies will be assigned from ${commaAndFormatStringList(
    unAllocatedCountryNames
  )}. Remaining ${(
    unallocatedProxiesCount - selectedCoutriesCount
  ).toLocaleString()} proxies will be assigned from all around the world.`;
}
